import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from "react-router-dom";
import swal from 'sweetalert';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faHourglassHalf, faLock, faUserGraduate } from '@fortawesome/free-solid-svg-icons';

import { calculateConflicts, getSchoolRunningProcesses, runStudentPlacement } from '../../../js/Helpers';
import { filterOutConflicts } from '../../../js/Conflicts';
import { CONFLICT_TYPES } from '../../../js/Constants';
import { FileUpload } from '../../../js/upload';
import { useConflictActions, useConflictData } from '../store/magnetboardStore';

const MONITOR_AFTER_SUBSCREEN = 'clean_duplicates';
const MONITOR_BEFORE_SUBSCREEN = 'place_students';
const SUPERVISOR_ROLE_ID = '3';

const MONITOR_STEP_LABELS = {
	manual_placements: 'Manual Placements',
	teacher_availability: 'Teacher Availability',
	course_availability: 'Course Availability',
	course_caps: 'Course Caps',
	course_lengths: 'Course Lengths',
	course_flags: 'Course Flags',
	course_teachers: 'Course Teachers',
	classroom_settings: 'Classroom Settings',
	course_classrooms: 'Course Classrooms',
	teacher_classrooms: 'Teacher Classrooms',
	create_sections: 'Create Sections',
	place_sections: 'Placement Review',
};

const STUDENT_PLACEMENT_CONFLICTS = [
	CONFLICT_TYPES.COURSE_EXCEEDS_CAPACITY,
	CONFLICT_TYPES.TEACHER_STUDENT_RESTRICTION,
	CONFLICT_TYPES.STUDENT_STUDENT_RESTRICTION,
	CONFLICT_TYPES.STUDENT_DOUBLE_BOOKED,
	CONFLICT_TYPES.STUDENT_CONFLICTING_PERIODS,
	CONFLICT_TYPES.INCLUSION_TOO_MANY_STUDENTS,
	CONFLICT_TYPES.INCLUSION_OVER_HALF,
];

const getStepOrder = (step = {}) => {
	const parsed_order = parseInt(step.subscreen_order || step.screen_order, 10);
	return Number.isNaN(parsed_order) ? 999 : parsed_order;
};

const getMonitorStepDefinitions = (appSubscreens = [], rolePages = {}) => {
	const source_steps = Array.isArray(appSubscreens) && appSubscreens.length > 0 ?
		appSubscreens :
		Object.entries(rolePages || {}).reduce((results, [screen, screen_info]) => {
			const subscreens = Array.isArray(screen_info?.subscreens) ? screen_info.subscreens : [];

			subscreens.forEach((subscreen_info, subscreen_index) => {
				results.push({
					...subscreen_info,
					screen,
					subscreen_index,
					screen_order: screen_info.screen_order,
				});
			});

			return results;
		}, []);

	const all_steps = source_steps.map((step, index) => ({
		...step,
		subscreen_index: step.subscreen_index ?? index,
	})).sort((step_a, step_b) => {
		if(getStepOrder(step_a) !== getStepOrder(step_b)) return getStepOrder(step_a) - getStepOrder(step_b);
		return step_a.subscreen_index - step_b.subscreen_index;
	});

	const anchor_index = all_steps.findIndex(step => step.subscreen === MONITOR_AFTER_SUBSCREEN);
	if(anchor_index === -1) return [];

	const stop_index = all_steps.findIndex(step => step.subscreen === MONITOR_BEFORE_SUBSCREEN);

	return all_steps.filter((step, step_index) => (
		step_index > anchor_index &&
		(stop_index === -1 || step_index < stop_index) &&
		`${step.responsible_role_id}` === SUPERVISOR_ROLE_ID
	));
};

const getMonitorSteps = (department = {}, monitor_step_definitions = []) => (
	monitor_step_definitions.map(step_definition => ({
		...step_definition,
		...(department.subscreens?.[step_definition.subscreen] || {}),
		screen: step_definition.screen,
		subscreen: step_definition.subscreen,
	}))
);

const getMonitorStepLabel = (step = {}) => (
	MONITOR_STEP_LABELS[step.subscreen] || step.subscreen?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || 'Next Step'
);

export default function PlaceStudents({ schoolSubscribed, subscreenRestricted, subscreenLocked, isLoading, schoolInfo, schoolType, scheduleInfo, userInfo, departmentsLoading, departments, appSubscreens, rolePages, subscreenSelect, getNewData, setLoadScreen }) {
	const [studentPlacementsHaveStarted, setStudentPlacementsHaveStarted] = useState(false);
	const [studentPlacementsFinished, setStudentPlacementsFinished] = useState(false);
	const [statusCheckComplete, setStatusCheckComplete] = useState(false);
	const [finalMatchPercent, setFinalMatchPercent] = useState(null);
	const { setConflicts, setConflictsLoading, setMatchPercent, setMatchPercentLoading, markConflictDataFresh } = useConflictActions();
	const { conflicts, matchPercent, matchPercentLoading } = useConflictData();
	const current_user_is_admin = (userInfo.role_id === '1' || userInfo.role_id === '2' || userInfo.role_id === '5');

	const monitorStepDefinitions = useMemo(() => getMonitorStepDefinitions(appSubscreens, rolePages), [appSubscreens, rolePages]);

	const departmentProgressRows = useMemo(() => {
		return (Array.isArray(departments) ? departments : []).map(department => {
			const monitor_steps = getMonitorSteps(department, monitorStepDefinitions);
			const submitted_steps = monitor_steps.filter(step => !!step.submitted);
			const submitted_count = submitted_steps.length;
			const total_count = monitor_steps.length;
			const percent_complete = total_count > 0 ? Math.round((submitted_count / total_count) * 100) : 0;
			const next_step = monitor_steps.find(step => !step.submitted);
			const complete = total_count > 0 && submitted_count === total_count;

			return {
				department_id: department.department_id,
				department: department.department,
				complete,
				percent_complete,
				submitted_count,
				total_count,
				next_step,
				progress_color: complete ? 'green' : (percent_complete <= 50 ? 'red' : 'yellow'),
				status_label: complete ? 'Ready for student placement' : getMonitorStepLabel(next_step),
			};
		});
	}, [departments, monitorStepDefinitions]);

	const allDepartmentsComplete = departmentProgressRows.length > 0 && departmentProgressRows.every(row => row.complete);
	const incompleteDepartmentCount = departmentProgressRows.filter(row => !row.complete).length;
	const placementIsRunning = studentPlacementsHaveStarted && !studentPlacementsFinished;
	const loadingActiveScheduleYear = scheduleInfo?.app_current_schedule_year_id !== undefined && schoolInfo?.current_schedule_year_id !== undefined && `${scheduleInfo.app_current_schedule_year_id}` === `${schoolInfo.current_schedule_year_id}`;
	const previousScheduleYearAutoSchedulingMessage = 'Sorry, you cannot run auto-scheduling features in previous scheduling years.';
	const placeStudentsDisabled = !loadingActiveScheduleYear || !allDepartmentsComplete || placementIsRunning || studentPlacementsFinished || !current_user_is_admin;
	const displayedMatchPercent = finalMatchPercent !== null ? finalMatchPercent : matchPercent;

	const refreshStudentPlacementResults = useCallback(async ({refreshData = true} = {}) => {
		if(refreshData && typeof getNewData === 'function') getNewData();

		setConflictsLoading(true);
		setMatchPercentLoading(true);

		try {
			const conflicts_response = await calculateConflicts({
				school_id: schoolInfo.school_id,
				schedule_version_id: schoolInfo.current_schedule_version_id,
				conflicts_to_check: STUDENT_PLACEMENT_CONFLICTS,
				match_percent: true,
			});
			const conflicts_list = conflicts_response?.conflicts || [];
			const filtered_out_conflicts = filterOutConflicts(conflicts, [], STUDENT_PLACEMENT_CONFLICTS);
			const next_match_percent = conflicts_response?.match_percent ?? 0;

			setConflicts([...filtered_out_conflicts, ...conflicts_list]);
			setMatchPercent(next_match_percent);
			setFinalMatchPercent(next_match_percent);
			markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id);
		} catch(error)
		{
			console.log(error.response || error);
		} finally
		{
			setConflictsLoading(false);
			setMatchPercentLoading(false);
		}
	}, [conflicts, getNewData, markConflictDataFresh, schoolInfo.current_schedule_version_id, schoolInfo.school_id, setConflicts, setConflictsLoading, setMatchPercent, setMatchPercentLoading]);

	const completeStudentPlacement = useCallback(async ({refreshData = true} = {}) => {
		setStudentPlacementsHaveStarted(true);
		setStudentPlacementsFinished(true);
		await refreshStudentPlacementResults({refreshData});
	}, [refreshStudentPlacementResults]);

	const placeStudents = async () => {
		if(!loadingActiveScheduleYear) return;
		if(placeStudentsDisabled) return;

		setStudentPlacementsHaveStarted(true);
		setStudentPlacementsFinished(false);

		try {
			await runStudentPlacement({
				school_id: schoolInfo.school_id,
				schedule_version_id: schoolInfo.current_schedule_version_id,
				data_version_id: schoolInfo.current_data_version_id,
			});

			const file_upload = new FileUpload(schoolInfo.current_data_version_id, schoolInfo.current_schedule_version_id, schoolInfo.school_id);
			const running_processes = await file_upload.getSchoolProcesses(schoolInfo.current_schedule_year_id);
			const process_info = running_processes.find(process => process.process_type === 'studentplace' && `${process.schedule_version_id}` === `${schoolInfo.current_schedule_version_id}`);
			const progress_id = process_info ? process_info.progress_id : null;

			if(!progress_id) throw new Error('Student placement progress id was not found.');

			await file_upload.checkUploading(schoolInfo.school_id, progress_id, async (event) => {
				if(event.failure)
				{
					setStudentPlacementsHaveStarted(false);
					setStudentPlacementsFinished(false);
					swal({
						icon: 'error',
						title: 'Error Placing Students',
						text: 'We had an issue placing your students. Please try again.',
						dangerMode: true,
					});
					return;
				}

				if(!event.done) return;

				await completeStudentPlacement();
			});
		} catch(error)
		{
			console.log(error.response || error);
			setStudentPlacementsHaveStarted(false);
			setStudentPlacementsFinished(false);
			swal({
				icon: 'error',
				title: 'Error Placing Students',
				text: 'We had an issue placing your students. Please try again.',
				dangerMode: true,
			});
		}
	};

	const goToCurrentStep = (row) => {
		if(!row.next_step || !row.next_step.subscreen || !row.next_step.screen) return;
		if(typeof subscreenSelect !== 'function') return;

		subscreenSelect(row.next_step.subscreen, row.next_step.screen, row.department_id);
	};

	useEffect(() => {
		if(statusCheckComplete) return;
		if(!schoolInfo.school_id || !schoolInfo.current_schedule_year_id || !schoolInfo.current_schedule_version_id) return;

		getSchoolRunningProcesses({school_id: schoolInfo.school_id, schedule_year_id: schoolInfo.current_schedule_year_id}).then(running_processes => {
			const progress_info = running_processes.find(process => process.process_type === 'studentplace' && `${process.schedule_version_id}` === `${schoolInfo.current_schedule_version_id}`);

			if(progress_info?.is_done)
			{
				completeStudentPlacement({refreshData: false});
			}
			else if(progress_info?.has_started)
			{
				setStudentPlacementsHaveStarted(true);
				const file_upload = new FileUpload(schoolInfo.current_data_version_id, schoolInfo.current_schedule_version_id, schoolInfo.school_id);
				file_upload.checkUploading(schoolInfo.school_id, progress_info.progress_id, async (event) => {
					if(event.failure)
					{
						setStudentPlacementsHaveStarted(false);
						setStudentPlacementsFinished(false);
						swal({
							icon: 'error',
							title: 'Error Placing Students',
							text: 'We had an issue placing your students. Please try again.',
							dangerMode: true,
						});
						return;
					}

					if(!event.done) return;

					await completeStudentPlacement();
				});
			}

			setStatusCheckComplete(true);
		}).catch(error => {
			console.log(error.response || error);
			setStatusCheckComplete(true);
		});
	}, [completeStudentPlacement, schoolInfo.current_data_version_id, schoolInfo.current_schedule_version_id, schoolInfo.current_schedule_year_id, schoolInfo.school_id, statusCheckComplete]);

	const renderStudentPlacementLoader = () => (
		<div className='place-students-running-container'>
			<div className='place-students-loader' aria-hidden='true'>
				{['pastel-blue', 'yellow', 'pastel-green', 'pastel-light-blue'].map((color, section_index) => (
					<div className='place-students-loader-section' key={section_index}>
						<div className={`place-students-loader-section-top ${color}`}></div>
						<div className='place-students-loader-section-body'>
							{[0, 1, 2].map(student_index => (
								<div className='place-students-loader-student' key={student_index}>
									<FontAwesomeIcon icon={faUserGraduate}/>
								</div>
							))}
						</div>
					</div>
				))}
			</div>
			<h2>Students are being placed...</h2>
			<div>We are matching student requests to available sections and checking schedule fit across the board.</div>
		</div>
	);

	const renderMonitorScreen = () => (
		<div className='place-students-monitor-container'>
			<div className='place-students-monitor-header'>
				<div>
					<h2>Department Progress</h2>
					<p>Student placement can begin after every department has completed its constraints, submitted sections, and completed placement review.</p>
				</div>
				<div className={`place-students-monitor-summary ${allDepartmentsComplete ? 'complete' : 'waiting'}`}>
					<FontAwesomeIcon icon={allDepartmentsComplete ? faCheckCircle : faHourglassHalf}/>
					<div>
						<div className='place-students-monitor-summary-value'>{allDepartmentsComplete ? 'Ready' : `${incompleteDepartmentCount} Working`}</div>
						<div className='place-students-monitor-summary-label'>{departmentProgressRows.length} Departments</div>
					</div>
				</div>
			</div>
			<div className='place-students-monitor-table'>
				<div className='place-students-monitor-table-header'>
					<div>Department</div>
					<div>Current Step</div>
					<div>Submitted</div>
					<div>Status</div>
				</div>
				{departmentProgressRows.map(row => (
					<div className='place-students-monitor-row' key={row.department_id}>
						<div className='place-students-monitor-department-name'>{row.department}</div>
						<div>
							{row.complete ?
								(
									<div className='place-students-monitor-step'>{row.status_label}</div>
								):
								(
									<div className='place-students-monitor-step place-students-monitor-step-link' onClick={() => goToCurrentStep(row)}>{row.status_label}</div>
								)
							}
						</div>
						<div className='place-students-monitor-progress-cell'>
							<div className='place-students-monitor-progress-meta'>
								<span>{row.percent_complete}%</span>
								<span>{row.submitted_count}/{row.total_count}</span>
							</div>
							<div className='place-students-monitor-progress-track'>
								<div className={`place-students-monitor-progress-fill ${row.progress_color}`} style={{width:`${row.percent_complete}%`}}></div>
							</div>
						</div>
						<div className={`place-students-monitor-status ${row.complete ? 'complete' : 'working'}`}>
							<FontAwesomeIcon icon={row.complete ? faCheckCircle : faHourglassHalf}/>
							<span>{row.complete ? 'Complete' : 'In Progress'}</span>
						</div>
					</div>
				))}
			</div>
			<div className='place-students-monitor-footer'>
				<div className='place-students-monitor-footer-note'>
					{!loadingActiveScheduleYear ? previousScheduleYearAutoSchedulingMessage : (allDepartmentsComplete ? 'All departments are ready for student placement.' : 'Waiting for every department to submit all required steps.')}
				</div>
				<div className={`btn place-students-monitor-action-btn ${placeStudentsDisabled ? 'btn-disabled' : 'green-btn'}`} onClick={placeStudents}>Place Students</div>
			</div>
		</div>
	);

	const renderSuccessScreen = () => (
		<div className='place-students-success-container'>
			<div className='place-students-success-heading'>
				<div className='place-students-success-icon'>
					<FontAwesomeIcon icon={faCheckCircle}/>
				</div>
				<div>
					<h2>Students Have Been Placed</h2>
					<p>Your initial student placement is complete. From here, you can take full control of the schedule in the Magnetboard.</p>
				</div>
			</div>
			<div className='place-students-match-card'>
				<div className='place-students-match-label'>Request Match</div>
				<div className='place-students-match-value'>{matchPercentLoading && finalMatchPercent === null ? 'Calculating...' : `${displayedMatchPercent}%`}</div>
			</div>
			<div className='place-students-caveats'>
				<h3>Here are a few things to keep in mind about this initial student placement:</h3>
				<ol>
					<li>This percentage is purely a percent of all requests matched, so it may not tell the full story about your schedule. You will want to look at individual course fulfillment to make sure it is meeting your scheduling priorities.</li>
					<li>This is a preliminary placement and you can usually get higher percentages by adjusting constraints and course priorities.</li>
					<li>We will guide you through how to look for misplaced constraints and identify spots for improvement in the Magnetboard.</li>
				</ol>
			</div>
			<div className='place-students-magnetboard-callout'>
				<div>From here on out, you can take full control of your schedule in the Magnetboard.</div>
				<Link className='btn btn-extra-large green-btn place-students-magnetboard-btn' to={`/app/${schoolType || 'hsms'}/magnetboard`} onClick={() => setLoadScreen('magnetboard')}>Go to Magnetboard</Link>
			</div>
		</div>
	);

	return (
		<div className="school-setup-main-container">
			{isLoading || departmentsLoading || !statusCheckComplete ?
			(
				<div className='center-text'>
					<img src={require('../../../images/balls.gif')} alt='Loading...' style={{height:'40px'}}/>
				</div>
			):(subscreenRestricted) ?
			(
				<div className='general-screen-message'>
					<FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/>
					<h1>No Access</h1>
					<div>You do not have access to this screen</div>
				</div>
			):(subscreenLocked) ?
			(
				<div className='general-screen-message'>
					<FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/>
					<h1>Temporarily Locked</h1>
					<div>This screen is locked until all other prior steps are completed</div>
				</div>
			):(!schoolSubscribed) ?
			(
				<div className='section-placement-screen-message' style={{maxWidth:'600px'}}>
					<img style={{height:'80px'}} src={require("../../../images/fill-students.png")} alt='Place students'/>
					<h2>Place Students into Sections</h2>
					<div>In this step, we will place students into sections, going through millions of different combinations to maximize the number of requests fulfilled.</div>
					<div style={{padding:'20px', backgroundColor:'#efefef', border:'2px solid #ddd', margin:'20px 0px 20px 0px', borderRadius:'10px'}}>
						<div>You need a paid subscription for this feature.</div>
						<div style={{margin:'6px 0px 0px 0px'}}>But don't let that stop you. You can still skip this step and keep working.</div>
						<div style={{fontSize:'18px', margin:'45px 0px 0px 0px'}}><strong>Please contact us at contact@edario.com to get full access in Edario.</strong></div>
					</div>
					<Link className="blue-link align-right" style={{maxWidth:'250px', margin:'0px 0px -30px 0px'}} to={`/app/${schoolType || 'hsms'}/magnetboard`} onClick={() => setLoadScreen('magnetboard')}>Skip to Magnetboard</Link>
				</div>
			):placementIsRunning ? (
				renderStudentPlacementLoader()
			):studentPlacementsFinished ? (
				renderSuccessScreen()
			):(
				renderMonitorScreen()
			)}
		</div>
	);
}
